import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { PageHeader, Section, Loading } from "../components/Common";
import { toast } from "sonner";
import { CalendarDays, Building2, Palette, GraduationCap, FileText, ShieldAlert, Landmark, History, Clock3, UsersRound } from "lucide-react";

const TABS = [
  { id: "institution", label: "Institution", icon: Building2 },
  { id: "calendar",    label: "Academic Calendar", icon: CalendarDays },
  { id: "structure",   label: "Academic Structure", icon: GraduationCap },
  { id: "timetable",   label: "Timetable Configuration", icon: Clock3 },
  { id: "pastoral",    label: "Pastoral House Review", icon: UsersRound },
  { id: "branding",    label: "Branding", icon: Palette },
  { id: "reporting",   label: "Reporting & Publishing", icon: FileText },
  { id: "audit",       label: "Audit History", icon: History },
];

const STATUS_LABEL = {
  in_term: "In term",
  in_holiday: "In holiday",
  in_exam_period: "Examination period",
  in_reporting_period: "Reporting period",
  before_academic_year: "Before academic year",
  after_academic_year: "After academic year",
  calendar_unconfigured: "Calendar unconfigured",
  override: "Administrative override in effect",
};

export default function SchoolAdmin() {
  const { tenant, user } = useAuth();
  const [tab, setTab] = useState("institution");
  const [current, setCurrent] = useState(null);
  const [config, setConfig] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);

  const canEdit = ["principal", "administrator", "board"].includes(user?.role);

  const refresh = () => {
    api.calendarCurrent().then(({ data }) => setCurrent(data)).catch(() => {});
    api.calendarConfig().then(({ data }) => setConfig(data)).catch(() => {});
    api.adminAudit().then(({ data }) => setAudit(data.entries || [])).catch(() => {});
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div data-testid="school-admin-page">
      <PageHeader
        kicker="School Administration"
        title="Tenant configuration console"
        subtitle={`Authoritative HIC configuration workspace. RBAC: ${canEdit ? "you may edit" : "read-only"}. Every change is audit-logged.`}
        actions={
          current && (
            <div className="flex items-center gap-2 text-xs">
              <div className="hl-chip hl-chip-dark px-3 py-1.5 rounded-full font-semibold" data-testid="sa-current-term">
                <CalendarDays className="inline w-3.5 h-3.5 mr-1" />
                AY {current.academic_year} · {current.current_term || "—"}
              </div>
              <span className="text-slate-500" data-testid="sa-current-status">{STATUS_LABEL[current.status] || current.status}</span>
            </div>
          )
        }
      />

      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition
                       ${tab === t.id ? "border-[color:var(--hl-orange)] text-[color:var(--hl-navy)]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            data-testid={`sa-tab-${t.id}`}>
            <t.icon className="inline w-4 h-4 mr-1.5 -mt-0.5" />{t.label}
          </button>
        ))}
      </div>

      {tab === "institution" && <InstitutionTab tenant={tenant} canEdit={canEdit} busy={busy} setBusy={setBusy} refresh={refresh} />}
      {tab === "calendar" && <CalendarTab current={current} config={config} canEdit={canEdit} busy={busy} setBusy={setBusy} refresh={refresh} />}
      {tab === "structure" && <StructureTab tenant={tenant} />}
      {tab === "timetable" && <TimetableConfigTab canEdit={canEdit} busy={busy} setBusy={setBusy} />}
      {tab === "pastoral" && <PastoralReviewTab canReview={["principal","administrator","board","deputy_head"].includes(user?.role)} />}
      {tab === "branding" && <BrandingTab tenant={tenant} canEdit={canEdit} busy={busy} setBusy={setBusy} refresh={refresh} />}
      {tab === "reporting" && <ReportingTab tenant={tenant} />}
      {tab === "audit" && <AuditTab entries={audit} />}
    </div>
  );
}

// ---------------- Institution tab ----------------
function InstitutionTab({ tenant, canEdit, busy, setBusy, refresh }) {
  const initial = useMemo(() => ({
    name: tenant?.name || "",
    short_name: tenant?.short_name || "",
    country: tenant?.country || "",
    province: tenant?.province || "",
    city: tenant?.city || "",
    suburb: tenant?.suburb || "",
    address: tenant?.address || "",
    phone: tenant?.phone || "",
    email: tenant?.email || "",
    website: tenant?.website || "",
    domain: tenant?.domain || "",
    motto: tenant?.motto || tenant?.branding?.motto || "",
    timezone: tenant?.timezone || "",
    currency: tenant?.currency || "",
    principal: tenant?.principal || "",
    principal_email: tenant?.principal_email || "",
    principal_phone: tenant?.principal_phone || "",
    admissions_email: tenant?.admissions_email || "",
    finance_email: tenant?.finance_email || "",
  }), [tenant]);
  const [form, setForm] = useState(initial);
  const [reason, setReason] = useState("");
  const changed = JSON.stringify(form) !== JSON.stringify(initial);

  useEffect(() => setForm(initial), [initial]);

  const save = async () => {
    setBusy(true);
    try {
      await api.adminUpdateInstitution({ ...form, reason });
      toast.success("Institution settings saved");
      setReason("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Section title="Institution">
      <div className="hl-card p-6 grid md:grid-cols-2 gap-4" data-testid="sa-institution-form">
        {[
          ["name", "School Name"], ["short_name", "Short Name"],
          ["country", "Country"], ["timezone", "Timezone"],
          ["province", "Province"], ["city", "City"],
          ["suburb", "Suburb"], ["address", "Postal Address"],
          ["phone", "Telephone"], ["email", "Institutional Email"],
          ["website", "Website"], ["domain", "Production Domain"],
          ["motto", "Motto"], ["currency", "Currency"],
          ["principal", "Principal"], ["principal_email", "Principal Email"],
          ["principal_phone", "Principal Phone"], ["admissions_email", "Admissions Email"],
          ["finance_email", "Finance Email"],
        ].map(([k, label]) => (
          <label key={k} className="text-sm">
            <div className="overline mb-1">{label}</div>
            <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              disabled={!canEdit} data-testid={`sa-field-${k}`}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50" />
          </label>
        ))}
      </div>
      <div className="mt-4 hl-card p-4 flex flex-wrap items-center gap-3">
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for change (optional)"
          disabled={!canEdit} data-testid="sa-institution-reason"
          className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50 min-w-[240px]" />
        <button onClick={save} disabled={!canEdit || !changed || busy}
          className="hl-btn-primary text-sm px-4 py-2 disabled:opacity-50" data-testid="sa-institution-save">
          {busy ? "Saving…" : "Save changes"}
        </button>
        <button onClick={() => setForm(initial)} disabled={!changed || busy}
          className="hl-btn-secondary text-sm px-3 py-2 disabled:opacity-50" data-testid="sa-institution-cancel">
          Cancel
        </button>
      </div>
    </Section>
  );
}

// ---------------- Calendar tab ----------------
function CalendarTab({ current, config, canEdit, busy, setBusy, refresh }) {
  const [ay, setAy] = useState("");
  const [tz, setTz] = useState("");
  const [terms, setTerms] = useState([]);
  const [overrideForm, setOverrideForm] = useState({ term_name: "", reason: "", effective_from: "", effective_until: "" });

  useEffect(() => {
    if (!config) return;
    setAy(config.academic_year || "");
    setTz(config.timezone || "");
    setTerms(config.terms || []);
  }, [config]);

  const saveConfig = async () => {
    setBusy(true);
    try {
      await api.calendarUpdate({ academic_year: ay, timezone: tz, terms });
      toast.success("Academic calendar saved");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const setOverride = async () => {
    if (!overrideForm.term_name || !overrideForm.reason || !overrideForm.effective_from || !overrideForm.effective_until) {
      toast.error("Term, reason and effective dates are required for override");
      return;
    }
    setBusy(true);
    try {
      await api.calendarOverride(overrideForm);
      toast.success("Override applied — audit entry created");
      setOverrideForm({ term_name: "", reason: "", effective_from: "", effective_until: "" });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Override failed");
    } finally { setBusy(false); }
  };

  const clearOverride = async () => {
    setBusy(true);
    try {
      await api.calendarOverrideClear();
      toast.success("Override cleared — automatic governance resumed");
      refresh();
    } finally { setBusy(false); }
  };

  if (!current) return <Loading />;

  return (
    <>
      <Section title="Current calendar state">
        <div className="hl-card p-6 grid md:grid-cols-4 gap-4" data-testid="sa-calendar-state">
          <Stat label="Academic Year" value={current.academic_year || "—"} />
          <Stat label="Current Term" value={current.current_term || "—"} highlight />
          <Stat label="Status" value={STATUS_LABEL[current.status] || current.status || "—"} />
          <Stat label="Timezone" value={current.timezone || "—"} />
          <Stat label="Term start" value={current.term_start_date || "—"} />
          <Stat label="Term end" value={current.term_end_date || "—"} />
          <Stat label="Next term" value={current.next_term || "—"} />
          <Stat label="Next term start" value={current.next_term_start || "—"} />
        </div>
        {current.override_active && (
          <div className="mt-3 hl-card p-4 border-l-4 border-amber-500 bg-amber-50" data-testid="sa-override-active">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <ShieldAlert className="w-4 h-4" />
              Administrative override — {current.override.term_name}
            </div>
            <div className="text-xs text-amber-800 mt-1">
              {current.override.reason} · effective {current.override.effective_from} → {current.override.effective_until} · by {current.override.authorised_by}
            </div>
            <button onClick={clearOverride} disabled={!canEdit || busy}
              className="mt-2 text-xs underline text-amber-900" data-testid="sa-override-clear">Clear override</button>
          </div>
        )}
      </Section>

      <Section title="Academic year & terms">
        <div className="hl-card p-6 space-y-4" data-testid="sa-calendar-editor">
          <div className="grid md:grid-cols-3 gap-4">
            <label className="text-sm"><div className="overline mb-1">Academic Year</div>
              <input value={ay} onChange={(e) => setAy(e.target.value)} disabled={!canEdit}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50"
                data-testid="sa-cal-ay" /></label>
            <label className="text-sm"><div className="overline mb-1">Timezone</div>
              <input value={tz} onChange={(e) => setTz(e.target.value)} disabled={!canEdit}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50"
                data-testid="sa-cal-tz" /></label>
            <label className="text-sm"><div className="overline mb-1">Next Academic Year</div>
              <input value={config?.next_academic_year || ""} disabled className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50" /></label>
          </div>
          <div className="space-y-2">
            {terms.map((t, i) => (
              <div key={i} className="grid md:grid-cols-6 gap-2 items-center text-sm" data-testid={`sa-cal-term-${i}`}>
                <div className="font-semibold text-[color:var(--hl-navy)]">{t.name}</div>
                <label className="text-xs">Start
                  <input type="date" value={t.start_date || ""} disabled={!canEdit}
                    onChange={(e) => setTerms(terms.map((x, xi) => xi === i ? { ...x, start_date: e.target.value } : x))}
                    className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm mt-1 disabled:bg-slate-50" /></label>
                <label className="text-xs">End
                  <input type="date" value={t.end_date || ""} disabled={!canEdit}
                    onChange={(e) => setTerms(terms.map((x, xi) => xi === i ? { ...x, end_date: e.target.value } : x))}
                    className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm mt-1 disabled:bg-slate-50" /></label>
                <label className="text-xs">Exam start
                  <input type="date" value={t.exam_start || ""} disabled={!canEdit}
                    onChange={(e) => setTerms(terms.map((x, xi) => xi === i ? { ...x, exam_start: e.target.value } : x))}
                    className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm mt-1 disabled:bg-slate-50" /></label>
                <label className="text-xs">Exam end
                  <input type="date" value={t.exam_end || ""} disabled={!canEdit}
                    onChange={(e) => setTerms(terms.map((x, xi) => xi === i ? { ...x, exam_end: e.target.value } : x))}
                    className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm mt-1 disabled:bg-slate-50" /></label>
                <label className="text-xs">Reporting start
                  <input type="date" value={t.reporting_start || ""} disabled={!canEdit}
                    onChange={(e) => setTerms(terms.map((x, xi) => xi === i ? { ...x, reporting_start: e.target.value } : x))}
                    className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm mt-1 disabled:bg-slate-50" /></label>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={saveConfig} disabled={!canEdit || busy} className="hl-btn-primary text-sm px-4 py-2 disabled:opacity-50" data-testid="sa-cal-save">
              {busy ? "Saving…" : "Save calendar"}
            </button>
          </div>
        </div>
      </Section>

      <Section title="Controlled override">
        <div className="hl-card p-6 space-y-3" data-testid="sa-override-form">
          <div className="text-xs text-slate-500">
            Overrides temporarily force a specific term regardless of the calendar. On expiry, automatic governance resumes. Every override is audit-logged.
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <label className="text-sm"><div className="overline mb-1">Term</div>
              <select value={overrideForm.term_name} onChange={(e) => setOverrideForm({ ...overrideForm, term_name: e.target.value })} disabled={!canEdit}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50" data-testid="sa-ov-term">
                <option value="">Select term…</option>
                {terms.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select></label>
            <label className="text-sm"><div className="overline mb-1">Effective from</div>
              <input type="date" value={overrideForm.effective_from} onChange={(e) => setOverrideForm({ ...overrideForm, effective_from: e.target.value })} disabled={!canEdit}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50" data-testid="sa-ov-from" /></label>
            <label className="text-sm"><div className="overline mb-1">Effective until</div>
              <input type="date" value={overrideForm.effective_until} onChange={(e) => setOverrideForm({ ...overrideForm, effective_until: e.target.value })} disabled={!canEdit}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50" data-testid="sa-ov-until" /></label>
            <label className="text-sm md:col-span-4"><div className="overline mb-1">Reason (required)</div>
              <input value={overrideForm.reason} onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })} disabled={!canEdit}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50" data-testid="sa-ov-reason" /></label>
          </div>
          <button onClick={setOverride} disabled={!canEdit || busy} className="hl-btn-secondary text-sm px-4 py-2 disabled:opacity-50" data-testid="sa-ov-apply">
            Apply override
          </button>
        </div>
      </Section>
    </>
  );
}

// ---------------- Structure / Branding / Reporting / Audit tabs ----------------
function StructureTab({ tenant }) {
  return (
    <Section title="Academic structure (read-only in Fork 1)">
      <div className="hl-card p-6 space-y-2 text-sm" data-testid="sa-structure">
        <Row label="Houses" value={(tenant?.houses || []).join(" · ") || "—"} />
        <Row label="Curricula" value={(tenant?.curricula || []).join(" · ") || "—"} />
        <Row label="School type" value={tenant?.school_type || "—"} />
        <Row label="Ownership" value={tenant?.ownership || "—"} />
        <Row label="Established" value={tenant?.established_year || tenant?.founded_year || "—"} />
        <Row label="Student capacity" value={tenant?.student_capacity || "—"} />
        <Row label="Target enrolment" value={tenant?.target_enrolment || "—"} />
      </div>
    </Section>
  );
}

function BrandingTab({ tenant, canEdit, busy, setBusy, refresh }) {
  const [primary, setPrimary] = useState(tenant?.branding?.primary_color || "#0F1F4A");
  const [accent, setAccent]   = useState(tenant?.branding?.accent_color  || "#F97316");
  const [motto, setMotto]     = useState(tenant?.branding?.motto || "");

  useEffect(() => {
    setPrimary(tenant?.branding?.primary_color || "#0F1F4A");
    setAccent(tenant?.branding?.accent_color  || "#F97316");
    setMotto(tenant?.branding?.motto || "");
  }, [tenant]);

  const save = async () => {
    setBusy(true);
    try {
      await api.adminUpdateBranding({ primary_color: primary, accent_color: accent, motto });
      toast.success("Branding saved");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Section title="Branding">
      <div className="hl-card p-6 grid md:grid-cols-3 gap-6" data-testid="sa-branding">
        <label className="text-sm"><div className="overline mb-1">Primary colour</div>
          <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} disabled={!canEdit} className="w-full h-10 border border-slate-200 rounded" data-testid="sa-brand-primary" />
          <div className="mono text-xs mt-1">{primary}</div></label>
        <label className="text-sm"><div className="overline mb-1">Accent colour</div>
          <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} disabled={!canEdit} className="w-full h-10 border border-slate-200 rounded" data-testid="sa-brand-accent" />
          <div className="mono text-xs mt-1">{accent}</div></label>
        <label className="text-sm md:col-span-1"><div className="overline mb-1">Motto</div>
          <input value={motto} onChange={(e) => setMotto(e.target.value)} disabled={!canEdit} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50" data-testid="sa-brand-motto" /></label>
      </div>
      <div className="mt-3">
        <button onClick={save} disabled={!canEdit || busy} className="hl-btn-primary text-sm px-4 py-2 disabled:opacity-50" data-testid="sa-brand-save">
          {busy ? "Saving…" : "Save branding"}
        </button>
      </div>
    </Section>
  );
}

function ReportingTab() {
  return (
    <Section title="Reporting & Publishing">
      <div className="hl-card p-6 text-sm text-slate-600" data-testid="sa-reporting">
        Configurable in a subsequent fork. Reporting periods are derived from the Academic Calendar
        (reporting_start / reporting_end on each configured term). Report Card templates, QR verification
        and publishing approval rules move here in Fork 3 of the closure programme.
      </div>
    </Section>
  );
}

function AuditTab({ entries }) {
  return (
    <Section title="Configuration audit history">
      <div className="hl-card p-0 overflow-hidden" data-testid="sa-audit-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="text-left p-3">Timestamp</th><th className="text-left p-3">Actor</th><th className="text-left p-3">Area</th><th className="text-left p-3">Reason</th><th className="text-left p-3">Change</th></tr>
          </thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">No audit entries yet.</td></tr>}
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50 align-top" data-testid={`sa-audit-row-${e.id}`}>
                <td className="p-3 mono text-xs">{e.timestamp}</td>
                <td className="p-3">{e.actor_email}<div className="text-xs text-slate-500">{e.actor_role}</div></td>
                <td className="p-3 mono text-xs">{e.area}</td>
                <td className="p-3 text-xs">{e.reason || "—"}</td>
                <td className="p-3 text-xs text-slate-600">
                  <div><span className="text-slate-400">from</span> {JSON.stringify(e.old_value).slice(0, 120)}</div>
                  <div><span className="text-slate-400">to</span> {JSON.stringify(e.new_value).slice(0, 120)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ---------------- Helpers ----------------
function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className={`font-display font-bold text-lg ${highlight ? "text-[color:var(--hl-orange)]" : "text-[color:var(--hl-navy)]"}`}>{value}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
      <div className="text-slate-500">{label}</div>
      <div className="text-slate-800 font-medium text-right">{value}</div>
    </div>
  );
}


// ---------------- Timetable Configuration tab (Fork 2A-5A) ----------------
function TimetableConfigTab({ canEdit, busy, setBusy }) {
  const [cfg, setCfg] = React.useState(null);
  const [audit, setAudit] = React.useState([]);
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState(null);
  const load = () => {
    api.adminTimetableConfigGet().then(({ data }) => setCfg(data.timetable_config || defaultTTC())).catch(() => {});
    api.adminTimetableAudit().then(({ data }) => setAudit(data.entries || [])).catch(() => {});
  };
  React.useEffect(() => { load(); }, []);
  if (!cfg) return <Loading />;

  const updatePeriod = (idx, field, value) => {
    const periods = [...cfg.periods];
    periods[idx] = { ...periods[idx], [field]: value };
    setCfg({ ...cfg, periods });
  };
  const addPeriod = () => {
    setCfg({ ...cfg, periods: [...(cfg.periods || []), { number: (cfg.periods?.length || 0) + 1, start: "14:15", end: "15:05", kind: "lesson" }] });
  };
  const removePeriod = (idx) => {
    const periods = cfg.periods.filter((_, i) => i !== idx).map((p, i) => ({ ...p, number: i + 1 }));
    setCfg({ ...cfg, periods });
  };
  const toggleDay = (day) => {
    const days = cfg.teaching_days.includes(day) ? cfg.teaching_days.filter((d) => d !== day) : [...cfg.teaching_days, day];
    const order = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
    setCfg({ ...cfg, teaching_days: order.filter((d) => days.includes(d)) });
  };
  const validate = () => {
    if (!Array.isArray(cfg.teaching_days) || cfg.teaching_days.length === 0) return "At least one teaching day required.";
    const nums = new Set();
    for (const p of cfg.periods || []) {
      if (nums.has(p.number)) return `Duplicate period number ${p.number}.`;
      nums.add(p.number);
      if (!p.start || !p.end) return "Every period must have start and end.";
      if (p.end <= p.start) return `Period ${p.number}: end (${p.end}) must be after start (${p.start}).`;
    }
    const sorted = [...(cfg.periods || [])].sort((a,b) => a.start.localeCompare(b.start));
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].start < sorted[i-1].end) return `Overlap between P${sorted[i-1].number} and P${sorted[i].number}.`;
    }
    return null;
  };
  const save = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    if (!reason || reason.length < 3) { setError("Reason is required (min 3 chars)."); return; }
    setBusy(true);
    try {
      const payload = { teaching_days: cfg.teaching_days, periods: cfg.periods, assembly: cfg.assembly, co_curricular_window: cfg.co_curricular_window, double_period_allowed: cfg.double_period_allowed, reason };
      await api.adminTimetableConfigPut(payload);
      toast.success("Timetable configuration saved");
      setError(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      setError(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const ALL_DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

  return (
    <div className="space-y-6" data-testid="sa-timetable-config">
      <Section title="Teaching week">
        <div className="flex flex-wrap gap-2">
          {ALL_DAYS.map((d) => (
            <button key={d} type="button" disabled={!canEdit}
              className={`px-3 py-1.5 rounded-full text-sm border ${cfg.teaching_days.includes(d) ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)]" : "bg-white border-slate-300 text-slate-600"}`}
              onClick={() => toggleDay(d)} data-testid={`ttc-day-${d}`}>{d}</button>
          ))}
        </div>
      </Section>

      <Section title="Periods" actions={canEdit && <button className="hl-btn-secondary text-xs" onClick={addPeriod} data-testid="ttc-add-period">+ Add period</button>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(cfg.periods || []).map((p, i) => (
            <div key={i} className="hl-card p-4" data-testid={`ttc-period-${p.number}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-[color:var(--hl-navy)]">Period {p.number}</div>
                {canEdit && <button className="text-red-600 text-xs" onClick={() => removePeriod(i)} data-testid={`ttc-remove-p${p.number}`}>Remove</button>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <label className="text-xs text-slate-500">Start
                  <input type="time" className="hl-input w-full" value={p.start} disabled={!canEdit} onChange={(e) => updatePeriod(i, "start", e.target.value)} data-testid={`ttc-p${p.number}-start`} />
                </label>
                <label className="text-xs text-slate-500">End
                  <input type="time" className="hl-input w-full" value={p.end} disabled={!canEdit} onChange={(e) => updatePeriod(i, "end", e.target.value)} data-testid={`ttc-p${p.number}-end`} />
                </label>
              </div>
              {p.break_before && (
                <div className="mt-2 text-xs text-slate-500">Break before: {p.break_before.label} ({p.break_before.start}–{p.break_before.end})</div>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Assembly & co-curricular">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="hl-card p-3">
            <div className="overline mb-1">Assembly</div>
            <div>{cfg.assembly?.day || "—"} · {cfg.assembly?.start}–{cfg.assembly?.end}</div>
          </div>
          <div className="hl-card p-3">
            <div className="overline mb-1">Co-curricular window</div>
            <div>{cfg.co_curricular_window?.start}–{cfg.co_curricular_window?.end}</div>
          </div>
          <div className="hl-card p-3">
            <div className="overline mb-1">Double periods</div>
            <div>{cfg.double_period_allowed ? "Allowed" : "Not allowed"}</div>
          </div>
        </div>
      </Section>

      {canEdit ? (
        <Section title="Save changes">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input className="hl-input md:col-span-2" placeholder="Reason (audit-logged)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ttc-reason" />
            <button className="hl-btn-primary" onClick={save} disabled={busy} data-testid="ttc-save">Save configuration</button>
          </div>
          {error && <div className="mt-3 text-sm text-red-600" data-testid="ttc-error">{error}</div>}
        </Section>
      ) : (
        <div className="hl-chip hl-chip-dark text-xs px-3 py-1.5 rounded-full inline-block" data-testid="ttc-readonly">Read-only — only Principal / Administrator / Board may edit.</div>
      )}

      <Section title="Configuration audit history">
        <div className="hl-card p-0 overflow-hidden" data-testid="ttc-audit">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="text-left p-3">Timestamp</th><th className="text-left p-3">Actor</th><th className="text-left p-3">Reason</th><th className="text-left p-3">Change</th></tr>
            </thead>
            <tbody>
              {audit.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-400">No timetable changes yet.</td></tr>}
              {audit.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="p-3 mono text-xs">{e.timestamp}</td>
                  <td className="p-3">{e.actor_email}<div className="text-xs text-slate-500">{e.actor_role}</div></td>
                  <td className="p-3 text-xs">{e.reason || "—"}</td>
                  <td className="p-3 text-xs text-slate-600">from {JSON.stringify(e.old_value).slice(0, 100)} → to {JSON.stringify(e.new_value).slice(0, 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
function defaultTTC() { return { teaching_days: ["Monday","Tuesday","Wednesday","Thursday","Friday"], periods: [], assembly: {}, co_curricular_window: {}, double_period_allowed: true }; }

// ---------------- Pastoral House Review Queue tab (Fork 2A-5A) ----------------
function PastoralReviewTab({ canReview }) {
  const [state, setState] = React.useState(null);
  const [filter, setFilter] = React.useState({ legacy: "", year: "", boarding: "", status: "requires_review", q: "" });
  const [selected, setSelected] = React.useState(null);
  const [disposition, setDisposition] = React.useState("Manicaland");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = () => api.pastoralQueue().then(({ data }) => setState(data)).catch(() => {});
  React.useEffect(() => { load(); }, []);
  if (!state) return <Loading />;

  const rows = state.queue.filter((s) => {
    if (filter.legacy && s.house_legacy !== filter.legacy) return false;
    if (filter.year && s.year_group !== filter.year) return false;
    if (filter.boarding === "boarder" && !s.boarding) return false;
    if (filter.boarding === "day" && s.boarding) return false;
    if (filter.status && s.house_status !== filter.status) return false;
    if (filter.q) {
      const q = filter.q.toLowerCase();
      const name = `${s.first_name || ""} ${s.last_name || ""} ${s.admission_no || ""}`.toLowerCase();
      if (!name.includes(q)) return false;
    }
    return true;
  });
  const legacies = Array.from(new Set(state.queue.map((s) => s.house_legacy).filter(Boolean)));
  const years = Array.from(new Set(state.queue.map((s) => s.year_group).filter(Boolean)));

  const act = async () => {
    if (!selected || !canReview) return;
    if (reason.length < 3) { toast.error("Reason required (min 3 chars)"); return; }
    setBusy(true);
    try {
      await api.pastoralReview(selected.id, { disposition, reason });
      toast.success(`Recorded disposition: ${disposition}`);
      setSelected(null); setReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Review failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="sa-pastoral-review">
      <Section title="Data quality summary">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Total students" value={state.counts.total} />
          <Stat label="Valid assigned" value={state.counts.valid_assigned} highlight />
          <Stat label="Requires review" value={state.counts.requires_review} />
          <Stat label="Unassigned (no legacy)" value={state.counts.unassigned_no_legacy} />
          <Stat label="Invalid" value={state.counts.invalid} />
        </div>
        <div className="text-xs text-slate-500 mt-3">Pastoral analytics denominators must exclude <em>requires_review</em> or clearly disclose them.</div>
      </Section>

      <Section title="Review queue" actions={<span className="text-xs text-slate-500">{rows.length} of {state.queue.length} shown</span>}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
          <input className="hl-input" placeholder="Search name / admission no." value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} data-testid="phr-q" />
          <select className="hl-input" value={filter.legacy} onChange={(e) => setFilter({ ...filter, legacy: e.target.value })} data-testid="phr-f-legacy">
            <option value="">All legacy values</option>
            {legacies.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className="hl-input" value={filter.year} onChange={(e) => setFilter({ ...filter, year: e.target.value })} data-testid="phr-f-year">
            <option value="">All year groups</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="hl-input" value={filter.boarding} onChange={(e) => setFilter({ ...filter, boarding: e.target.value })} data-testid="phr-f-boarding">
            <option value="">Boarding + Day</option>
            <option value="boarder">Boarders only</option>
            <option value="day">Day only</option>
          </select>
          <select className="hl-input" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="phr-f-status">
            <option value="">Any status</option>
            <option value="requires_review">Requires review</option>
            <option value="assigned">Assigned</option>
            <option value="evidence_insufficient">Evidence insufficient</option>
          </select>
        </div>
        <div className="hl-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wider">
              <tr>
                <th className="text-left p-3">Student</th>
                <th className="text-left p-3">Adm no.</th>
                <th className="text-left p-3">Year</th>
                <th className="text-left p-3">B/D</th>
                <th className="text-left p-3">Legacy house</th>
                <th className="text-left p-3">Current</th>
                <th className="text-left p-3">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 60).map((s) => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`phr-row-${s.id}`}>
                  <td className="p-3">{s.first_name} {s.last_name}</td>
                  <td className="p-3 mono text-xs">{s.admission_no || "—"}</td>
                  <td className="p-3 text-xs">{s.year_group || "—"}</td>
                  <td className="p-3 text-xs">{s.boarding ? "Boarder" : "Day"}</td>
                  <td className="p-3 text-xs"><span className="hl-chip">{s.house_legacy || "—"}</span></td>
                  <td className="p-3 text-xs">{s.house || "—"}</td>
                  <td className="p-3 text-xs">{s.house_status || "—"}</td>
                  <td className="p-3 text-right"><button className="hl-btn-secondary text-xs" onClick={() => setSelected(s)} data-testid={`phr-open-${s.id}`}>Review</button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-400">No records match the current filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="phr-drawer">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Pastoral house review</div>
                <div className="font-display font-bold text-xl text-[color:var(--hl-navy)]">{selected.first_name} {selected.last_name}</div>
                <div className="text-xs text-slate-500">Admission {selected.admission_no || "—"} · {selected.year_group} · {selected.boarding ? "Boarder" : "Day"}</div>
              </div>
              <button className="text-slate-400" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
              <Row label="Legacy house value" value={selected.house_legacy || "—"} />
              <Row label="Current canonical" value={selected.house || "—"} />
              <Row label="Status" value={selected.house_status || "—"} />
              <Row label="Last updated" value={selected.updated_at || "—"} />
            </div>
            {selected.latest_review && (
              <div className="text-xs text-slate-500 mb-4 p-3 bg-slate-50 rounded">
                <div className="font-semibold text-slate-700">Latest audit entry</div>
                <div>{selected.latest_review.at} · {selected.latest_review.actor} · {selected.latest_review.reason}</div>
              </div>
            )}
            {canReview ? (
              <div className="space-y-3">
                <div>
                  <div className="overline mb-2">Disposition</div>
                  <div className="grid grid-cols-3 gap-2">
                    {["Mashonaland","Matabeleland","Manicaland","Midlands","requires_review","evidence_insufficient"].map((d) => (
                      <button key={d} onClick={() => setDisposition(d)}
                        className={`px-3 py-2 rounded border text-sm ${disposition === d ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)]" : "bg-white text-slate-600 border-slate-300"}`}
                        data-testid={`phr-disp-${d}`}>{d}</button>
                    ))}
                  </div>
                </div>
                <input className="hl-input" placeholder="Reason (audit-logged, min 3 chars)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="phr-reason" />
                <div className="text-right">
                  <button className="hl-btn-primary" onClick={act} disabled={busy} data-testid="phr-submit">Record disposition</button>
                </div>
              </div>
            ) : (
              <div className="hl-chip hl-chip-dark text-xs inline-block px-3 py-1.5 rounded-full" data-testid="phr-readonly">Read-only — only Principal / Administrator / Board / Deputy Head may review.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
